import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Typography, Grid, Card, CardContent,
  CardActions, Chip, IconButton, CircularProgress,
} from "@mui/material";
import { Add, Edit, Delete, OpenInNew } from "@mui/icons-material";
import axios from "axios";
import Header from "components/Header";

const CaseStudies = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCases = async () => {
    try {
      const res = await axios.get(`${apiUrl}/case-studies`);
      setCases(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCases(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this case study?")) return;
    try {
      await axios.delete(`${apiUrl}/case-studies/${id}`);
      setCases((prev) => prev.filter((c) => c._id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      const res = await axios.patch(`${apiUrl}/case-studies/${id}/publish`);
      setCases((prev) =>
        prev.map((c) =>
          c._id === id ? { ...c, isPublished: res.data.data.isPublished } : c
        )
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Header title="CASE STUDIES" subtitle="Manage your case study detail pages" />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/case-studies/create")}
          sx={{
            background: "#3b82f6",
            "&:hover": { background: "#2563eb" },
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
          }}
        >
          Create New
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {cases.map((cs) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={cs._id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "12px",
                }}
              >
                {/* Thumbnail */}
                <Box
                  sx={{
                    height: 140,
                    background: "rgba(255,255,255,0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "12px 12px 0 0",
                    overflow: "hidden",
                  }}
                >
                  {cs.thumbnailUrl ? (
                    <img
                      src={cs.thumbnailUrl}
                      alt={cs.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No Thumbnail
                    </Typography>
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} noWrap>
                    {cs.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    /{cs.slug}
                  </Typography>
                  <Chip
                    label={cs.isPublished ? "Published" : "Draft"}
                    size="small"
                    sx={{
                      background: cs.isPublished
                        ? "rgba(46,125,50,0.15)"
                        : "rgba(255,255,255,0.08)",
                      color: cs.isPublished ? "#66bb6a" : "text.secondary",
                      fontSize: "11px",
                    }}
                  />
                  {cs.category && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {cs.category}
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: "wrap" }}>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/case-studies/edit/${cs._id}`)}
                    sx={{ color: "text.secondary", "&:hover": { color: "#3b82f6" } }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(cs._id)}
                    color="error"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                  {cs.siteUrl && (
                    <IconButton
                      size="small"
                      component="a"
                      href={cs.siteUrl}
                      target="_blank"
                      sx={{ color: "text.secondary" }}
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleTogglePublish(cs._id)}
                    sx={{
                      textTransform: "none",
                      borderRadius: "6px",
                      fontSize: "11px",
                      ml: "auto",
                      borderColor: cs.isPublished ? "#66bb6a" : "rgba(255,255,255,0.2)",
                      color: cs.isPublished ? "#66bb6a" : "text.secondary",
                    }}
                  >
                    {cs.isPublished ? "Unpublish" : "Publish"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}

          {cases.length === 0 && (
            <Box width="100%" textAlign="center" mt={6}>
              <Typography color="text.secondary">
                No case studies yet. Create your first one.
              </Typography>
            </Box>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default CaseStudies;